import React from 'react';
import styled from 'styled-components';
import { Check } from 'lucide-react';

interface CheckboxProps {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  label?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  required?: boolean;
  name?: string;
  id?: string;
}

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`;

const sizePx = (size: string) => {
  switch (size) {
    case 'sm': return '16px';
    case 'lg': return '20px';
    default: return '18px';
  }
};

const CheckboxWrapper = styled.div<{ $disabled: boolean; $size: string }>`
  position: relative;
  flex-shrink: 0;
  width: ${props => sizePx(props.$size)};
  height: ${props => sizePx(props.$size)};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
`;

const HiddenCheckbox = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  opacity: 0;
  z-index: 2;
  cursor: inherit;
`;

const StyledCheckbox = styled.div<{ 
  $checked: boolean; 
  $disabled: boolean; 
  $error: boolean;
  $size: string;
}>`
  width: ${props => {
    switch (props.$size) {
      case 'sm': return '16px';
      case 'lg': return '20px';
      default: return '18px';
    }
  }};
  height: ${props => {
    switch (props.$size) {
      case 'sm': return '16px';
      case 'lg': return '20px';
      default: return '18px';
    }
  }};
  border: 2px solid ${props => {
    if (props.$error) return props.theme.destructive;
    if (props.$checked) return props.theme.primary;
    return props.theme.border;
  }};
  border-radius: 3px;
  background: ${props => {
    if (props.$disabled) return props.theme.muted;
    if (props.$checked) return props.theme.primary;
    return props.theme.background;
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${props => props.theme.transitionSmooth};
  
  &:hover {
    border-color: ${props => {
      if (props.$disabled) return props.theme.border;
      if (props.$error) return props.theme.destructive;
      return props.theme.primary;
    }};
  }
  
  svg {
    color: ${props => props.theme.background};
    width: ${props => {
      switch (props.$size) {
        case 'sm': return '10px';
        case 'lg': return '14px';
        default: return '12px';
      }
    }};
    height: ${props => {
      switch (props.$size) {
        case 'sm': return '10px';
        case 'lg': return '14px';
        default: return '12px';
      }
    }};
    opacity: ${props => props.$checked ? 1 : 0};
    transition: opacity 0.2s ease;
  }
`;

const LabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
`;

const Label = styled.label<{ $disabled: boolean; $error: boolean; $required: boolean }>`
  font-size: 0.875rem;
  color: ${props => {
    if (props.$disabled) return props.theme.mutedForeground;
    if (props.$error) return props.theme.destructive;
    return props.theme.foreground;
  }};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  gap: 0.25rem;
  
  &::after {
    content: ${props => props.$required ? '"*"' : '""'};
    color: ${props => props.theme.destructive};
  }
`;

const HelperText = styled.span<{ $error: boolean }>`
  font-size: 0.75rem;
  color: ${props => props.$error ? props.theme.destructive : props.theme.mutedForeground};
`;

export const Checkbox: React.FC<CheckboxProps> = ({
  checked = false,
  onChange,
  label,
  disabled = false,
  error = false,
  helperText,
  size = 'md',
  className,
  required = false,
  name,
  id,
}) => {
  const checkboxId = id || name || `checkbox-${Math.random().toString(36).substr(2, 9)}`;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    onChange?.(event.target.checked);
  };

  return (
    <CheckboxContainer className={className}>
      <CheckboxWrapper $disabled={disabled} $size={size}>
        <HiddenCheckbox
          id={checkboxId}
          name={name}
          type="checkbox"
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          required={required}
        />
        <StyledCheckbox
          $checked={checked}
          $disabled={disabled}
          $error={error}
          $size={size}
          aria-hidden
        >
          <Check />
        </StyledCheckbox>
      </CheckboxWrapper>
      
      {(label || helperText) && (
        <LabelContainer>
          {label && (
            <Label 
              htmlFor={checkboxId}
              $disabled={disabled}
              $error={error}
              $required={required}
            >
              {label}
            </Label>
          )}
          
          {helperText && (
            <HelperText $error={error}>
              {helperText}
            </HelperText>
          )}
        </LabelContainer>
      )}
    </CheckboxContainer>
  );
}; 